// ============================================================
// Revenue Report Page — 門店報表（營收日曆 + KPI）
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { shiftMonth, taipeiThisMonth, taipeiToday } from '../lib/datetime';

interface CalendarDay {
  date: string;
  dayOfWeek: number;
  revenue: number;
  laborCost: number;
  staffCount: number;
  scheduleCount: number;
  laborPct: number;
}

interface ReportData {
  month: string;
  daysInMonth: number;
  calendar: CalendarDay[];
  summary: {
    totalRevenue: number;
    totalLaborCost: number;
    laborPct: number;
    avgDailyRevenue: number;
    daysWithRevenue: number;
    avgRevenueByWeekday: number[];
    peakDay: CalendarDay;
    personnelCostThreshold?: number;  // 從老闆 App boss_app_settings 讀；舊版 API 沒回傳則 fallback 35
  };
}

const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六'];

export function RevenueReportPage() {
  const [month, setMonth] = useState(taipeiThisMonth);
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<ReportData>(`/revenue-report?month=${month}`);
      setData(res);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const navigateMonth = (delta: number) => setMonth(m => shiftMonth(m, delta));

  const [y, m] = month.split('-').map(Number);
  const monthLabel = `${y} 年 ${m} 月`;

  // 建構日曆 grid（前面補空白格）
  const firstDayOfWeek = data ? new Date(y, m - 1, 1).getDay() : 0;
  const calendarCells: (CalendarDay | null)[] = [];
  for (let i = 0; i < firstDayOfWeek; i++) calendarCells.push(null);
  if (data) {
    for (const day of data.calendar) calendarCells.push(day);
  }

  // 找出最高營收用於色階
  const maxRevenue = data ? Math.max(...data.calendar.map(d => d.revenue), 1) : 1;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
      <h1 className="text-xl font-bold text-morandi-800">門店報表</h1>

      {/* 資料來源（議題 2 中期切換完成）*/}
      <div className="rounded-card border border-sage-300 bg-sage-50 px-4 py-3 text-sm text-sage-700">
        資料來源：<b>POS（v_daily_revenue）</b>。本頁為唯讀，營收以 POS 實際成交為準（已過濾作廢/退款）。
      </div>

      {/* 月份選擇 */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigateMonth(-1)}
          className="w-9 h-9 rounded-full flex items-center justify-center text-morandi-500 hover:bg-morandi-100"
        >
          ‹
        </button>
        <span className="text-lg font-semibold text-morandi-700 min-w-[120px] text-center">{monthLabel}</span>
        <button
          onClick={() => navigateMonth(1)}
          className="w-9 h-9 rounded-full flex items-center justify-center text-morandi-500 hover:bg-morandi-100"
        >
          ›
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-morandi-400">載入中...</div>
      ) : data ? (
        <>
          {/* KPI 摘要 */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KpiCard label="月營收" value={`$${data.summary.totalRevenue.toLocaleString()}`} />
            <KpiCard label="日均營收" value={`$${data.summary.avgDailyRevenue.toLocaleString()}`} />
            <KpiCard label="人事成本" value={`$${data.summary.totalLaborCost.toLocaleString()}`} />
            {(() => {
              const threshold = data.summary.personnelCostThreshold ?? 35;
              const isOver = data.summary.laborPct > threshold;
              return (
                <KpiCard
                  label="人事佔比"
                  value={`${data.summary.laborPct}%`}
                  highlight={isOver}
                  sub={isOver ? `超過 ${threshold}% 警戒線` : ''}
                />
              );
            })()}
            <KpiCard
              label="營收天數"
              value={`${data.summary.daysWithRevenue}`}
              sub={`/ ${data.daysInMonth} 天`}
            />
          </div>

          {/* 營收日曆 */}
          <div className="card-morandi p-4">
            <h2 className="font-semibold text-morandi-700 mb-3">營收日曆</h2>

            {/* 星期標頭 */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {WEEKDAY_LABELS.map((label, i) => (
                <div
                  key={label}
                  className={`text-center text-xs font-medium py-1 ${i === 0 || i === 6 ? 'text-rose-400' : 'text-morandi-400'}`}
                >
                  {label}
                </div>
              ))}
            </div>

            {/* 日期格 */}
            <div className="grid grid-cols-7 gap-1">
              {calendarCells.map((day, idx) => {
                if (!day) {
                  return <div key={`empty-${idx}`} className="aspect-square" />;
                }
                const dateNum = new Date(day.date).getDate();
                const isWeekend = day.dayOfWeek === 0 || day.dayOfWeek === 6;
                const intensity = day.revenue > 0 ? Math.max(0.1, day.revenue / maxRevenue) : 0;
                const isToday = day.date === taipeiToday();

                return (
                  <div
                    key={day.date}
                    className={`
                      aspect-square rounded-lg p-1.5 flex flex-col justify-between text-xs relative
                      ${isToday ? 'ring-2 ring-earth-400' : ''}
                      ${isWeekend ? 'bg-morandi-50' : 'bg-white'}
                    `}
                    style={day.revenue > 0 ? {
                      backgroundColor: `rgba(139, 164, 139, ${intensity * 0.3})`,
                    } : undefined}
                  >
                    <div className={`font-medium ${isWeekend ? 'text-rose-400' : 'text-morandi-600'}`}>
                      {dateNum}
                    </div>
                    {day.revenue > 0 ? (
                      <div className="text-right">
                        <div className="font-mono text-sage-700 font-medium" style={{ fontSize: '10px' }}>
                          ${day.revenue >= 10000 ? `${(day.revenue / 10000).toFixed(1)}萬` : day.revenue.toLocaleString()}
                        </div>
                        {day.laborPct > 0 && (
                          <div className={`font-mono ${day.laborPct > 35 ? 'text-rose-400' : 'text-morandi-300'}`} style={{ fontSize: '9px' }}>
                            {day.laborPct}%
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-right text-morandi-200" style={{ fontSize: '10px' }}>-</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 各星期平均營收 */}
          <div className="card-morandi p-4">
            <h2 className="font-semibold text-morandi-700 mb-3">各星期平均營收</h2>
            <div className="grid grid-cols-7 gap-2">
              {WEEKDAY_LABELS.map((label, i) => {
                const avg = data.summary.avgRevenueByWeekday[i];
                const maxAvg = Math.max(...data.summary.avgRevenueByWeekday, 1);
                const barHeight = avg > 0 ? Math.max(10, (avg / maxAvg) * 80) : 4;
                return (
                  <div key={label} className="flex flex-col items-center gap-1">
                    <div className="text-xs font-mono text-morandi-600">
                      {avg > 0 ? `$${(avg / 1000).toFixed(0)}k` : '-'}
                    </div>
                    <div className="w-full flex justify-center">
                      <div
                        className="w-8 rounded-t-md bg-sage-300"
                        style={{ height: `${barHeight}px` }}
                      />
                    </div>
                    <div className={`text-xs ${i === 0 || i === 6 ? 'text-rose-400' : 'text-morandi-400'}`}>
                      {label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 尖峰日 */}
          {data.summary.peakDay && data.summary.peakDay.revenue > 0 && (
            <div className="card-morandi p-4">
              <h2 className="font-semibold text-morandi-700 mb-2">本月尖峰日</h2>
              <div className="flex items-center gap-4">
                <div className="text-2xl font-bold font-mono text-sage-600">
                  ${data.summary.peakDay.revenue.toLocaleString()}
                </div>
                <div className="text-sm text-morandi-500">
                  {new Date(data.summary.peakDay.date).toLocaleDateString('zh-TW', { month: 'long', day: 'numeric', weekday: 'long' })}
                </div>
                {data.summary.peakDay.staffCount > 0 && (
                  <div className="text-sm text-morandi-400">
                    {data.summary.peakDay.staffCount} 人出勤
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-10 text-morandi-300">無資料</div>
      )}
    </div>
  );
}

function KpiCard({ label, value, highlight, sub }: {
  label: string;
  value: string;
  highlight?: boolean;
  sub?: string;
}) {
  return (
    <div className={`card-morandi p-3 text-center ${highlight ? 'ring-1 ring-rose-300' : ''}`}>
      <div className={`text-lg font-bold font-mono ${highlight ? 'text-rose-500' : 'text-morandi-700'}`}>{value}</div>
      <div className="text-xs text-morandi-400 mt-0.5">{label}</div>
      {sub && <div className="text-xs text-rose-400 mt-0.5">{sub}</div>}
    </div>
  );
}
